import { tuningBadge } from '../../data/tunings.js';
import React, { useState, useRef, useEffect } from 'react';
import { Music, Heart, Play, MoreHorizontal, FolderPlus, Trash2, RefreshCw, PencilLine } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { TabSong } from '../../types/index.js';
import { ModerationBadge } from './ModerationBadge.js';
import { RejectReasonNote } from './RejectReasonNote.js';
import { TabBackupBadge } from './TabBackupBadge.js';
import { DifficultySummary } from './DifficultyMeter.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

export interface TabCardProps {
  tab: TabSong;
  isFavorite: boolean;
  onToggleFavorite: (tabId: string) => void;
  onOpen: (tabId: string) => void;
  onAddToPlaylist?: (tabId: string) => void;
  onDelete: (tabId: string) => void;
  /** FRE-142: signed-out (guest) session; changes the backup badge text and hides the retry action. */
  isGuest?: boolean;
  onRetryBackup?: (tabId: string) => void;
  onEditDetails?: (tab: TabSong) => void;
}

const formatTuningBadge = tuningBadge;

export const TabCard: React.FC<TabCardProps> = ({
  tab,
  isFavorite,
  onToggleFavorite,
  onOpen,
  onAddToPlaylist,
  onDelete,
  isGuest = false,
  onRetryBackup,
  onEditDetails
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const trackCount = tab.tracks && tab.tracks.length > 0 ? tab.tracks.length : 1;

  return (
    <article
      data-testid={`tab-card-${tab.id}`}
      className="bg-base-200 border border-base-300 rounded-2xl p-4 flex flex-col justify-between gap-3.5 transition hover:border-primary/40 hover:shadow-md"
    >
      {/* Top Row: Icon, Title & Artist, Favorite Heart */}
      <div className="flex items-start gap-3">
        <span className="size-11 shrink-0 rounded-xl bg-neutral text-primary-soft flex items-center justify-center">
          <Music className="size-5" />
        </span>
        <div className="flex-1 min-w-0 text-start">
          <p
            dir="auto"
            data-testid={`tab-card-title-${tab.id}`}
            className="text-[15px] font-semibold text-base-content truncate"
          >
            <bdi dir="auto">{tab.title}</bdi>
          </p>
          <p dir="auto" className="text-[13px] text-base-content/60 truncate mt-0.5">
            <bdi dir="auto">{tab.artist}</bdi>
          </p>
        </div>
        <IconButton
          data-testid={`tab-card-favorite-${tab.id}`}
          label={isFavorite ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}
          title={isFavorite ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}
          icon={<Heart className={`size-4.5 ${isFavorite ? 'fill-current' : ''}`} />}
          variant="ghost"
          size="sm"
          className={`size-11 min-w-[44px] min-h-[44px] rounded-xl ${
            isFavorite
              ? 'text-error-soft hover:text-error'
              : 'text-base-content/40 hover:text-error-soft'
          }`}
          onClick={() => onToggleFavorite(tab.id)}
        />
      </div>

      {/* Badges: Tuning + Track count */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          dir="ltr"
          className="h-6 px-2.5 rounded-full bg-neutral border border-base-300 font-mono ltr-nums text-[11px] text-base-content/80 inline-flex items-center select-none"
        >
          {formatTuningBadge(tab.tuning)}
        </span>
        <span className="h-6 px-2.5 rounded-full bg-neutral border border-base-300 text-xs text-base-content/80 inline-flex items-center select-none">
          {toPersianDigits(trackCount)} ترک
        </span>
        <ModerationBadge status={tab.status} tabId={tab.id} />
        <TabBackupBadge tab={tab} isGuest={isGuest} />
        <DifficultySummary rhythm={tab.rhythmDifficulty} lead={tab.leadDifficulty} size="sm" />
      </div>
      <RejectReasonNote tab={tab} />

      {/* Bottom Actions Row: Open button + More options */}
      <div className="flex items-center gap-2 pt-3 border-t border-base-300">
        <Button
          variant="secondary"
          size="sm"
          className="flex-1 text-[13px] font-semibold h-9 md:h-9 md:min-h-9 rounded-xl"
          leadingIcon={<Play className="size-3.5 fill-current" />}
          onClick={() => onOpen(tab.id)}
          data-testid={`tab-card-open-${tab.id}`}
        >
          باز کردن
        </Button>

        <div className="relative" ref={menuRef}>
          <IconButton
            label="گزینه‌های بیشتر"
            title="گزینه‌های بیشتر"
            icon={<MoreHorizontal className="size-4" />}
            variant="secondary"
            size="sm"
            className="size-11 min-w-[44px] min-h-[44px] md:size-9 md:min-h-9 md:min-w-9 rounded-xl text-base-content/60 hover:text-base-content"
            data-testid={`tab-card-more-${tab.id}`}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-expanded={menuOpen}
          />
          {menuOpen && (
            <div
              role="menu"
              className="absolute end-0 bottom-full mb-1.5 z-30 min-w-[170px] p-1.5 rounded-xl bg-base-200 border border-base-300 shadow-xl flex flex-col gap-0.5 select-none"
            >
              <button
                type="button"
                role="menuitem"
                data-testid="tab-menu-details"
                onClick={() => {
                  setMenuOpen(false);
                  onEditDetails?.(tab);
                }}
                className="flex items-center gap-2.5 w-full px-2.5 py-2 text-xs font-medium rounded-lg text-base-content/80 hover:bg-base-300 hover:text-base-content transition text-start"
              >
                <PencilLine className="size-4 shrink-0 text-primary-soft" />
                <span data-testid={`tab-menu-details-${tab.id}`}>ویرایش مشخصات</span>
              </button>
              {onAddToPlaylist && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onAddToPlaylist(tab.id);
                  }}
                  className="flex items-center gap-2.5 w-full px-2.5 py-2 text-xs font-medium rounded-lg text-base-content/80 hover:bg-base-300 hover:text-base-content transition text-start"
                >
                  <FolderPlus className="size-4 shrink-0 text-primary-soft" />
                  <span>افزودن به لیست پخش</span>
                </button>
              )}
              {onRetryBackup && !isGuest && tab.pendingUpload === 1 && tab.uploadBlocked === 1 && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid={`tab-card-retry-backup-${tab.id}`}
                  onClick={() => {
                    setMenuOpen(false);
                    onRetryBackup(tab.id);
                  }}
                  className="flex items-center gap-2.5 w-full px-2.5 py-2 text-xs font-medium rounded-lg text-base-content/80 hover:bg-base-300 hover:text-base-content transition text-start"
                >
                  <RefreshCw className="size-4 shrink-0 text-primary-soft" />
                  <span>تلاش دوباره برای پشتیبان‌گیری</span>
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                data-testid={`tab-menu-delete-${tab.id}`}
                onClick={() => {
                  setMenuOpen(false);
                  onDelete(tab.id);
                }}
                className="flex items-center gap-2.5 w-full px-2.5 py-2 text-xs font-medium rounded-lg text-error-soft hover:bg-error/10 hover:text-error transition text-start"
              >
                <Trash2 className="size-4 shrink-0" />
                <span>حذف از حافظه</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

TabCard.displayName = 'TabCard';
